import React from 'react';
import { parseEntityRef } from '@backstage/catalog-model';
import { EmptyState } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import {
  fetchData,
  urls,
} from '@internal/backstage-plugin-dh-dock-shared-common';

const useStyles = makeStyles(theme => ({
  Active: { color: theme.palette.success.main },
  Expired: { color: theme.palette.error.main },
  Unavailable: { color: 'inherit' },
}));

const GeneratePatPage = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const [token, setToken] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(false);
  const [historyVersion, setHistoryVersion] = React.useState(0);
  const [historyLoading, setHistoryLoading] = React.useState(true);
  const [historyError, setHistoryError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [userTokens, setUserTokens] = React.useState<
    { jti: string; email: string; createdAt: string | null; expiresAt: string | null }[]
  >([]);

  React.useEffect(() => {
    let active = true;
    const loadTokens = async () => {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const identity = await identityApi.getBackstageIdentity();
        const { name: email } = parseEntityRef(identity.userEntityRef);
        const data = await fetchData(
          `${urls.getPATByEmail}?email=${encodeURIComponent(email)}`,
        );
        if (active) {
          setUserTokens(data ? [{
            jti: data.jti,
            email: data.email,
            createdAt: data.created_at,
            expiresAt: data.expires_at || data.expiry_at,
          }] : []);
        }
      } catch {
        if (active) {
          setHistoryError('Failed to load tokens');
        }
      } finally {
        if (active) {
          setHistoryLoading(false);
        }
      }
    };
    loadTokens();
    return () => {
      active = false;
    };
  }, [identityApi, historyVersion]);

  const formatDate = (value: string | null) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime())
      ? date.toLocaleString()
      : 'Unavailable';
  };

  const getTokenStatus = (expiresAt: string | null) => {
    const expiryTime = expiresAt ? new Date(expiresAt).getTime() : NaN;
    if (Number.isNaN(expiryTime)) {
      return 'Unavailable';
    }
    return expiryTime > Date.now() ? 'Active' : 'Expired';
  };

  const handleGenerateToken = async () => {
    try {
      setLoading(true);
      setCooldown(true);
      setError(null);
      setToken(null);
      const data = await fetchData(urls.generateUserPat);
      if (!data?.token) {
        throw new Error('No token returned from server');
      }
      setToken(data.token);
      setCopied(false);
      setHistoryVersion(previous => previous + 1);
      setTimeout(() => setCooldown(false), 30000);
    } catch {
      setError('Failed to generate token');
      setCooldown(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteToken = async (jti: string) => {
    if (!window.confirm('Are you sure you want to delete this token?')) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await fetchData(
        `${urls.deletePAT}?jti=${encodeURIComponent(jti)}`,
        {},
        false,
        true,
      );
      if (!result?.success) {
        throw new Error('Token was not deleted');
      }
      setUserTokens(previous => previous.filter(userToken => userToken.jti !== jti));
      setToken(null);
      setCopied(false);
      setCooldown(false);
      setHistoryLoading(true);
      setHistoryVersion(previous => previous + 1);
    } catch {
      setDeleteError('Failed to delete token');
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyToken = async () => {
    if (!token) {
      return;
    }
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
    <Card>
      <CardHeader title="Generate your DH Dock Token" />
      <Divider />
      <CardContent>
        <Typography variant="body2">
          The generated token expires after 30 days.
        </Typography>
        <br />
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateToken}
          disabled={loading || cooldown || historyLoading || deleting || userTokens.length > 0 || Boolean(token)}
        >
          {loading ? 'Generating...' : 'Generate Token'}
        </Button>
        {error && <Typography color="error">{error}</Typography>}
        {token && (
          <>
            <br />
            <br />
            <TextField
              fullWidth
              multiline
              minRows={4}
              variant="outlined"
              label="Your bearer token"
              value={token}
              InputProps={{ readOnly: true }}
            />
            <br />
            <br />
            <Button variant="outlined" onClick={handleCopyToken}>
              {copied ? 'Copied!' : 'Copy Token'}
            </Button>
            <Typography variant="body2">
              <br />
              Expires on:{' '}
              {
                new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                  .toLocaleString()
                  .split(',')[0]
              }
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
      <Box mt={3}>
        <Card>
          <CardHeader
            title="Token History"
            titleTypographyProps={{ component: 'h2', id: 'user-tokens-heading' }}
          />
          <Divider />
          <CardContent>
        {deleteError && <Typography color="error" role="alert">{deleteError}</Typography>}
        {!historyLoading && !historyError && userTokens.length === 0 ? (
          <EmptyState missing="data" title="No tokens found" />
        ) : (
        <TableContainer>
          <Table size="small" aria-labelledby="user-tokens-heading">
            {(historyLoading || historyError || userTokens.length > 0) && <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Date created</TableCell>
                <TableCell>Expiry date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>}
            <TableBody>
              {historyLoading || historyError || userTokens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {historyLoading ? 'Loading tokens...' : historyError || (
                      <EmptyState missing="data" title="No tokens found" />
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                userTokens.map((userToken, index) => (
                  <TableRow key={index}>
                    <TableCell component="th" scope="row">
                      {userToken.email || 'Unavailable'}
                    </TableCell>
                    <TableCell>{formatDate(userToken.createdAt)}</TableCell>
                    <TableCell>{formatDate(userToken.expiresAt)}</TableCell>
                    <TableCell className={classes[getTokenStatus(userToken.expiresAt)]}>
                      {getTokenStatus(userToken.expiresAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete token">
                        <span>
                          <IconButton
                            aria-label={`Delete token for ${userToken.email}`}
                            size="small"
                            disabled={deleting || !userToken.jti}
                            onClick={() => handleDeleteToken(userToken.jti)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export { GeneratePatPage };
